import React, { useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'

import classnames from 'classnames'

import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { t } from 'src/translations'
import { ModifierGroup } from 'public/components/online_ordering/types'

import Image from 'shared/components/common/Image'
import { DoneIcon, ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import { ModifierOverflowModal } from './ModifierOverflowModal'
import { ModifierGroupSelections } from './ModifierGroupSelections'
import { getModifierGroupSubtitle } from './getModifierGroupSubtitle'
import { useA11y } from 'public/components/online_ordering/useA11y'

type ListProps = {
  group?: ModifierGroup
  firstModifierGroup?: boolean
  /** When set and there are more modifiers, only this many show inline; the rest open in a modal. */
  inlineOptionLimit?: number
  /** Visual variant - 'modal' (default) for OO modal context, 'pdp' for PDP page context */
  variant?: 'modal' | 'pdp'
}

const SelectionList = ({
  group,
  firstModifierGroup,
  inlineOptionLimit,
  variant = 'modal'
}: ListProps) => {
  const [isOverflowModalOpen, setIsOverflowModalOpen] = useState(false)
  const { isValid, isInvalid, registerModifierGroup, getGroupSelectionsCount } =
    useModifierContext()
  const { announce } = useA11y()
  const lastAnnouncedRef = useRef<string>('')

  useEffect(() => {
    if (!group) return
    const { guid, name, minSelections, maxSelections } = group
    if (!isInvalid(guid)) {
      lastAnnouncedRef.current = ''
      return
    }
    const currentSelectionsCount = getGroupSelectionsCount(guid)
    let msg = ''
    if (minSelections && maxSelections) {
      msg = t('selection-list.select-between', {
        minSelections,
        maxSelections,
        name,
        currentSelectionsCount
      })
    } else if (minSelections && currentSelectionsCount) {
      msg = t('selection-list.select-at-least-aria', {
        minSelections,
        name,
        currentSelectionsCount
      })
    } else {
      msg = t('selection-list.please-make-selection', { name })
    }
    if (msg && msg !== lastAnnouncedRef.current) {
      lastAnnouncedRef.current = msg
      announce(msg)
    }
  }, [getGroupSelectionsCount, group, isInvalid, announce])

  const valid = useMemo(
    () => (group ? isValid(group.guid) : false),
    [isValid, group]
  )
  const invalid = useMemo(
    () => (group ? isInvalid(group.guid) : false),
    [isInvalid, group]
  )

  if (!group) {
    return null
  }

  const { guid, name, modifiers, minSelections, maxSelections } = group

  const inlineLimit =
    typeof inlineOptionLimit === 'number' && inlineOptionLimit > 0
      ? inlineOptionLimit
      : null
  const overflowCount =
    inlineLimit && modifiers.length > inlineLimit
      ? modifiers.length - inlineLimit
      : 0
  const inlineModifiers =
    inlineLimit && overflowCount > 0
      ? modifiers.slice(0, inlineLimit)
      : modifiers

  const inputType =
    typeof minSelections === 'number' &&
    typeof maxSelections === 'number' &&
    minSelections === maxSelections &&
    maxSelections === 1
      ? 'radio'
      : 'checkbox'

  const label = `menu-group-${guid}`
  const allModsOutOfStock = modifiers.every((mod) => mod.outOfStock)
  const required = Boolean(minSelections && minSelections > 0)

  return (
    <div
      className={classnames('modSection', { pdp: variant === 'pdp' })}
      {...registerModifierGroup(guid)}
      data-testid='selection-list'
    >
      <div className='title'>
        <div className='modSectionTitleContainer'>
          <div className='modSectionTitle' id={`menu-group-${guid}`}>
            <h3>{name || <Skeleton />}</h3>
            {required && !allModsOutOfStock && (
              <div className={classnames('required', { valid, invalid })}>
                {valid ? (
                  <DoneIcon accessibility='decorative' />
                ) : invalid ? (
                  <Image alt='Warning icon' src='icons/warning-outline.svg' />
                ) : null}
                {t('selection-list.required')}
              </div>
            )}
          </div>
          <div className='modSectionSubtitle'>
            {getModifierGroupSubtitle(minSelections, maxSelections)}
          </div>
        </div>
      </div>
      <div
        className='selections'
        role={inputType === 'radio' ? 'radiogroup' : 'group'}
        aria-labelledby={label}
        aria-required={inputType === 'radio' ? required : undefined}
        data-testid='selections'
      >
        {modifiers ? (
          <>
            <ModifierGroupSelections
              groups={inlineModifiers}
              group={group}
              inputType={inputType}
              firstModifierGroup={
                inlineModifiers.length === 0 && !!firstModifierGroup
              }
            />
            {overflowCount > 0 ? (
              <>
                <div className='divider' aria-hidden='true' />
                <button
                  type='button'
                  className='pdpModifiersViewMoreButton'
                  data-testid='pdp-modifiers-view-more'
                  onClick={() => setIsOverflowModalOpen(true)}
                >
                  <span className='pdpModifiersViewMoreText'>
                    {t('pdp-modifiers.view-more-options', {
                      count: overflowCount
                    })}
                  </span>
                  <ChevronRightIcon accessibility='decorative' />
                </button>
              </>
            ) : null}
          </>
        ) : (
          new Array(10).fill(0).map((_, index) => (
            <div className='row skeleton' key={`skeleton${index}`}>
              <Skeleton width='100%' />
            </div>
          ))
        )}
      </div>

      {overflowCount > 0 ? (
        <ModifierOverflowModal
          isOpen={isOverflowModalOpen}
          onClose={() => setIsOverflowModalOpen(false)}
          group={group}
          overflowModifiers={modifiers}
          inputType={inputType}
        />
      ) : null}
    </div>
  )
}

export default SelectionList
